/**
 * Roll edit records up into the version snapshots they led to.
 *
 * Versions are the restorable checkpoints: auto-snapshots after 2h idle or 4h
 * of continuous editing, plus manual saves. Edits are the attributed changes
 * between them. An edit belongs to the first version created at or after it
 * *ended*. Edits newer than every version form the unsaved "current" group at
 * the top.
 */

import type { EditDiff } from "./diff";

export interface EditAuthor {
  id: string;
  name: string;
  email?: string;
}

export interface EditRecord extends EditDiff {
  id: string;
  /** Epoch ms. */
  startedAt: number;
  /** Epoch ms. */
  endedAt: number;
  authors: EditAuthor[];
}

export interface VersionSummary {
  id: string;
  label: string | null;
  authorId: string;
  trigger: string;
  createdAt: string | number;
}

export interface HistoryGroup {
  /** `null` for edits made since the most recent version. */
  version: VersionSummary | null;
  /** Epoch ms: the version's creation, or the newest edit for the current group. */
  at: number;
  edits: EditRecord[];
  /** Everyone who edited in this group, most edits first. */
  authors: EditAuthor[];
  totals: EditDiff["totals"];
}

export function rollUpHistory(versions: VersionSummary[], edits: EditRecord[]): HistoryGroup[] {
  const vs = [...versions].sort((a, b) => toMs(b.createdAt) - toMs(a.createdAt));
  const es = [...edits].sort((a, b) => b.endedAt - a.endedAt);

  const groups: HistoryGroup[] = [];
  let e = 0;

  // Edits newer than the newest version.
  const newest = vs[0] ? toMs(vs[0].createdAt) : Number.POSITIVE_INFINITY;
  const current: EditRecord[] = [];
  while (e < es.length && (vs.length === 0 || es[e]!.endedAt > newest)) current.push(es[e++]!);
  if (current.length) groups.push(group(null, current[0]!.endedAt, current));

  for (let v = 0; v < vs.length; v++) {
    const floor = vs[v + 1] ? toMs(vs[v + 1]!.createdAt) : Number.NEGATIVE_INFINITY;
    const bucket: EditRecord[] = [];
    while (e < es.length && es[e]!.endedAt > floor) bucket.push(es[e++]!);
    groups.push(group(vs[v]!, toMs(vs[v]!.createdAt), bucket));
  }

  return groups;
}

function group(version: VersionSummary | null, at: number, edits: EditRecord[]): HistoryGroup {
  const counts = new Map<string, { author: EditAuthor; n: number }>();
  const totals = { added: 0, removed: 0, changed: 0 };
  for (const edit of edits) {
    totals.added += edit.totals.added;
    totals.removed += edit.totals.removed;
    totals.changed += edit.totals.changed;
    for (const author of edit.authors) {
      const entry = counts.get(author.id) ?? { author, n: 0 };
      entry.n++;
      counts.set(author.id, entry);
    }
  }
  const authors = [...counts.values()].sort((a, b) => b.n - a.n).map((c) => c.author);
  return { version, at, edits, authors, totals };
}

/** Versions arrive as ISO strings (drizzle timestamp → JSON) or epoch numbers. */
function toMs(v: string | number): number {
  if (typeof v === "number") return v < 1e12 ? v * 1000 : v;
  return new Date(v).getTime();
}
